import styled from '@emotion/styled';

import { Link, useNavigate } from 'react-router-dom';

import { Grid, Card, CardHeader, CardContent, IconButton, Button, Tooltip } from '@mui/material';

import { CardBlockContainer, CardBlock, CardBlockHeader, CardSpacer } from '../../../utils/utilities/CardComponents';

import MultiStatusChip from '../../../utils/utilities/MultiStatusChip';

import SimpleChip from '../../../utils/utilities/SimpleChip';

import { relativeTime, formatPhoneNumber } from '../../../utils/util';

import { BasicFlex } from '../../../utils/Common';

import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import AccountBoxOutlinedIcon from '@mui/icons-material/AccountBoxOutlined';
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded';
import PhoneAndroidRoundedIcon from '@mui/icons-material/PhoneAndroidRounded';
import EditOutlined from '@mui/icons-material/EditOutlined';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';

import NoTimeline from '../../../utils/utilities/Timeline/NoTimeline';

import TimelineWithComments from '../../../utils/utilities/Timeline/TimelineWithComments';

import handleErrors from '../../../utils/errorHandler';

import EditCustomer from '../../Customers/EditCustomer';


// use pageData context
import { usePageData } from '../../../contexts/PageData';

import InvoiceDisplay from '../Components/InvoiceDisplay';

import Dialog from '../../../utils/utilities/Dialog';

import ShareCard from '../../../utils/utilities/Buttons/ShareCard';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteInvoice } from '../apiService';
import useSnackbarStore from '../../../store/snackbar';

const DeleteMessage = styled.div`
  padding: 0 1.6rem;
  line-height: 1.6;
`


const DetailsTab = () => {
  const { data = {} } = usePageData();

  let customer = data?.customer || {};

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const showSnackbar = useSnackbarStore(state => state.showSnackbar);

  const { mutate: deleteInvoiceMutation } = useMutation({
    mutationFn: deleteInvoice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      navigate('/invoices');
    },
    onError: (error) => showSnackbar(handleErrors(error), 'error'),
  });

  const handleDelete = () => deleteInvoiceMutation(data.invoice_id);

  const customerBlock = [
    {header: "Customer", icon: <AccountBoxOutlinedIcon fontSize='small' />, info: <BasicFlex gap="1rem">{customer.full_name} <Dialog title="Edit Customer Info" size="lg" toggle={<Tooltip title="Edit Customer Info"><Button size="small" color="basic" sx={{minWidth: 0, px: ".5rem"}}><EditOutlined fontSize="small" /></Button></Tooltip>} saveText="Delete" useForm={true}>
        <EditCustomer ignore_id={true} edit_id={customer.customer_id} dialog={true} invalidate_queries={[['invoices']]} />
      </Dialog></BasicFlex>
    },
    {header: "Phone", icon: <PhoneAndroidRoundedIcon fontSize='small' />, info: formatPhoneNumber(customer.mobile_phone)},
  ]

  const basicBlock = [
    {header: "Invoice ID", icon: <ConfirmationNumberRoundedIcon fontSize='small' color="basic" />, info: `#${data.invoice_num}`},
    {header: "Date", info: relativeTime(data.date)},
  ]

  const invoiceBlock = [
    {header: "Invoice Total",  info: data.total, dataType: "currency"},
    {header: "Paid Status", info: <MultiStatusChip value={data.paid_status} values={[{value: "unpaid", label: "Unpaid", status: "error"}, {value: "partially_paid", label: "Partially Paid", status: "warning"}, {value: "paid", label: "Paid", status: "success"}]} />},
    {header: "Paid Amount", info: data.total_paid, dataType: "currency"},
    {header: "Balance", info: data.balance, dataType: "currency"},
  ]

  return (
    <Grid container spacing={3}>
      <Grid size={{xs: 12, md: 8}}>
        <Grid container spacing={3}>
          <Grid size={{xs: 12}}>
            <Card variant='outlined'>
              <CardHeader title="Invoice Summary" action={<Link to={`/invoices/edit/${data.invoice_id}`}><IconButton color="secondary"><EditOutlined /></IconButton></Link>} />
              <CardContent>
                <CardBlockContainer>
                  {customerBlock.map((block) => (
                    <CardBlock key={block.header} {...block} />
                  ))}

                  <CardSpacer />
                  <CardBlockHeader>General Details</CardBlockHeader>
                  {basicBlock.map((block) => (
                    <CardBlock key={block.header} {...block} />
                  ))}

                  <CardSpacer />
                  <CardBlockHeader>Invoice Details</CardBlockHeader>
                  {invoiceBlock.map((block) => (
                    <CardBlock key={block.header} {...block} />
                  ))}
                  <CardSpacer />
                </CardBlockContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{xs: 12}}>
            <InvoiceDisplay invoice={data} />
          </Grid>
        </Grid>
      </Grid>

      <Grid size={{xs: 12, md: 4}}>
        <Dialog title="Confirm Cancel Invoice?" size="sm" toggle={<Button size="large" variant="contained" startIcon={<DeleteForeverRoundedIcon />} fullWidth sx={{backgroundColor: "error.lighter", mb: 2}}>Delete Invoice</Button>} saveText="Delete"  saveFunc={() => handleDelete()}>
        <DeleteMessage>
          Deleting this invoice will permanently remove it from the system. <br /> The original Payments attached to the invoice will NOT be deleted, only the applied payments will be deleted. This will leave the customer with a negative balance if there are any payments applied to the invoice.<br /> Are you sure you want to continue?
        </DeleteMessage>
        </Dialog>
        <ShareCard url={`/invoices/generate/${data.invoice_id}`} defaultEmail={customer?.email} title="Invoice Actions" />
        <Card variant='outlined' sx={{mt: 2}}>
          <CardHeader title={<BasicFlex gap="1rem">Invoice History <HistoryRoundedIcon /></BasicFlex>} />
          <CardContent>
            <TimelineWithComments resource="invoice" resource_id={data.invoice_id} expand={true} />
          </CardContent>
        </Card>
      </Grid>

      

 
    </Grid>
  );

};

export default DetailsTab;